// src/components/dashboards/filters/barFilterUrl.ts
//
// Filter-bar selections live in the URL (plan 2026-10-05 dashboard
// interactivity, A4), so a shared link reproduces the view:
// `?f.channel=Paid%20Search&f.channel=Email`. One parameter per VALUE, read
// with `getAll`: never a comma-joined list, campaign names contain commas.
// Click (tab) filters are view state and never land here.
//
// Validated like a control value (ADR-0323 decision 3, `utils/controlUrl.ts`):
// a key no tile on the dashboard can be filtered on leaves the URL with one
// notice. Pure, so the rules are testable without mounting Insights.
import type { DimensionFilters } from "@/api/dashboards";

export const BAR_PARAM_PREFIX = "f.";

/** The backend's caps on open viewer filters (`_validate_dimension_filters`):
 *  a link over them would make every tile a 422, so the excess is cut here. */
export const MAX_BAR_KEYS = 10;
export const MAX_BAR_VALUES = 50;
export const MAX_BAR_VALUE_LENGTH = 256;

/** Every `f.<key>` in the URL with its values, in URL order, deduplicated. */
export function readBarFilterParams(params: URLSearchParams): DimensionFilters {
  const out: DimensionFilters = {};
  for (const name of new Set(params.keys())) {
    if (!name.startsWith(BAR_PARAM_PREFIX)) continue;
    const key = name.slice(BAR_PARAM_PREFIX.length);
    if (!key) continue;
    const values = Array.from(new Set(params.getAll(name).filter(Boolean)));
    if (values.length) out[key] = values;
  }
  return out;
}

export type BarFilterResolution = {
  /** The bar filters the URL asks for that this dashboard can apply. */
  values: DimensionFilters;
  /** Keys whose parameters must leave the URL. */
  strip: string[];
  /** The part of `strip` the viewer is told about: keys no tile on this
   *  dashboard can be filtered by. A key cut only by the caps (a hand-made
   *  link) leaves without a notice. */
  rejected: string[];
};

/** The URL's bar filters, kept only for `allowedKeys` (the keys the filter
 *  bar offers on this dashboard). */
export function resolveBarFilterParams(
  params: URLSearchParams,
  allowedKeys: ReadonlySet<string>,
): BarFilterResolution {
  const values: DimensionFilters = {};
  const strip: string[] = [];
  const rejected: string[] = [];
  for (const [key, raw] of Object.entries(readBarFilterParams(params))) {
    if (!allowedKeys.has(key)) {
      strip.push(key);
      rejected.push(key);
      continue;
    }
    const kept = raw.filter((v) => v.length <= MAX_BAR_VALUE_LENGTH).slice(0, MAX_BAR_VALUES);
    if (!kept.length || Object.keys(values).length >= MAX_BAR_KEYS) {
      strip.push(key);
      continue;
    }
    values[key] = kept;
  }
  return { values, strip, rejected };
}

/** The URL after the bar set `key` to `values` (empty removes the filter).
 *  Every other parameter stays as it was. */
export function withBarFilterParam(
  prev: URLSearchParams,
  key: string,
  values: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const name = `${BAR_PARAM_PREFIX}${key}`;
  next.delete(name);
  for (const value of values) next.append(name, value);
  return next;
}

/** The URL with every `f.<key>` of `keys` removed. */
export function withoutBarFilterParams(prev: URLSearchParams, keys: readonly string[]): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of keys) next.delete(`${BAR_PARAM_PREFIX}${key}`);
  return next;
}

/** What the viewer is told when the link's filter could not be applied. */
export function rejectedBarFilterNotice(
  keys: readonly string[],
  /** The kind's nouns for the fixed words only; the keys are quoted as they are. */
  noun: string = "dashboard",
): string {
  return keys.length === 1
    ? `This link filtered on "${keys[0]}", which no tile on this ${noun} can be filtered by. That filter was removed.`
    : `This link filtered on ${keys.map((k) => `"${k}"`).join(", ")}, which no tile on this ${noun} can be filtered by. Those filters were removed.`;
}
